/**
 * Cartie SDK — Auto-injection landing page
 * =========================================
 * Shows the Python + TypeScript SDK source, install snippet, usage examples,
 * and a copy-to-clipboard button per file. Pulls source from /api/sdk/index.
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Code2, Copy, Check, Download, ArrowRight, Sparkles, Loader2, Terminal, Package } from 'lucide-react';
import BackToHomePill from '../components/BackToHomePill';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Canonical brand URL for curl examples — never leaks the API host (which
// may be a preview / staging domain). Users always curl from cartieai.com.
const BRAND_URL = 'https://cartieai.com';

const QUICK_PY = `import cartie
cartie.set_proxy(key="ck_live_...")
cartie.set_tenant("marketing")

client = cartie.openai()                 # drop-in for openai.OpenAI()
client.chat.completions.create(
    model="gpt-5.2",
    messages=[{"role": "user", "content": "Draft a blog headline"}],
)

# Egress gate (refuse cross-cloud bleed):
with cartie.egress(source="gcp", dest="aws", data_gb=1024,
                   token_pct=0.05, enforce=True):
    # If verdict is rose, proxy returns 412 BEFORE the upstream call.
    client.chat.completions.create(model="gemini-3-flash", ...)`;

const QUICK_TS = `import * as cartie from './cartie';
cartie.setProxy({ key: process.env.CARTIE_KEY! });
cartie.setTenant('marketing');

const client = cartie.openai({ apiKey: process.env.OPENAI_KEY! });
await client.chat.completions.create({
  model: 'gpt-5.2',
  messages: [{ role: 'user', content: 'Hello' }],
});

// Egress gate inside an async scope:
await cartie.withEgress(
  { source: 'gcp', dest: 'aws', dataGB: 1024, tokenPct: 0.05, enforce: true },
  () => client.chat.completions.create({ model: 'gemini-3-flash', ... }),
);`;

const SdkPage = () => {
  const [sdks, setSdks] = useState(null);
  const [active, setActive] = useState('python');
  const [copied, setCopied] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Cartie SDK · auto-inject X-Cartie headers in your code | CARTIEAI';
    axios.get(`${API_URL}/sdk/index`)
      .then((r) => setSdks(r.data?.sdks || []))
      .catch(() => setSdks([]))
      .finally(() => setLoading(false));
  }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) { /* clipboard blocked */ }
  };

  const current = sdks?.find((s) => s.language === active);
  const downloadUrl = current?.download_url
    ? `${process.env.REACT_APP_BACKEND_URL}${current.download_url}`
    : null;

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="sdk-page">
      <Helmet><title>Cartie SDK · CARTIEAI</title></Helmet>
      <BackToHomePill />

      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[380px] h-[380px] rounded-full bg-teal-500/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 mb-5 bg-emerald-500/10">
            <Package className="w-3 h-3" /> Drop-in · auto-injection · MIT-licensed
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white" data-testid="sdk-h1">
            One <span className="text-emerald-400">drop-in</span> client.<br />Every Cartie header — automatic.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
            Stop pasting headers on every request. Set <code className="text-emerald-300 font-mono bg-slate-900 px-1.5 py-0.5 rounded">cartie.set_tenant("marketing")</code> once at startup. Every subsequent call to OpenAI / Anthropic carries the attribution + egress gate headers automatically.
          </p>
          <p className="mt-3 text-sm text-slate-400">
            Single-file Python + TypeScript SDKs. Zero dependencies beyond the upstream LLM client. <strong className="text-white">Just <code className="text-emerald-300 font-mono bg-slate-900 px-1.5 py-0.5 rounded">curl</code> into your repo.</strong>
          </p>
        </div>

        {/* QUICKSTART */}
        <div className="mt-12 grid lg:grid-cols-2 gap-6" data-testid="sdk-quickstart">
          <div className="rounded-xl border border-emerald-500/30 bg-slate-900/60 p-5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
              <Terminal className="w-3 h-3 inline mr-1" /> Python · in 3 lines
            </div>
            <pre className="rounded-lg border border-emerald-500/20 bg-slate-950/80 p-4 text-[11px] leading-relaxed font-mono text-emerald-200 overflow-x-auto whitespace-pre" data-testid="sdk-quick-py">
{`curl -O ${BRAND_URL}/api/sdk/cartie.py
pip install openai
# That's it. import cartie in your code.`}
            </pre>
            <pre className="mt-3 rounded-lg border border-white/10 bg-slate-950/80 p-4 text-[11px] leading-relaxed font-mono text-slate-200 overflow-x-auto whitespace-pre" data-testid="sdk-usage-py">
{QUICK_PY}
            </pre>
          </div>

          <div className="rounded-xl border border-emerald-500/30 bg-slate-900/60 p-5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
              <Terminal className="w-3 h-3 inline mr-1" /> TypeScript · in 3 lines
            </div>
            <pre className="rounded-lg border border-emerald-500/20 bg-slate-950/80 p-4 text-[11px] leading-relaxed font-mono text-emerald-200 overflow-x-auto whitespace-pre" data-testid="sdk-quick-ts">
{`curl -O ${BRAND_URL}/api/sdk/cartie.ts
npm install openai
# That's it. import * as cartie from './cartie' in your code.`}
            </pre>
            <pre className="mt-3 rounded-lg border border-white/10 bg-slate-950/80 p-4 text-[11px] leading-relaxed font-mono text-slate-200 overflow-x-auto whitespace-pre" data-testid="sdk-usage-ts">
{QUICK_TS}
            </pre>
          </div>
        </div>

        {/* WHAT GETS AUTO-INJECTED */}
        <div className="mt-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">What gets auto-injected</h2>
          <p className="mt-2 text-sm text-slate-400 max-w-3xl">Every header below is added to your OpenAI / Anthropic calls when the corresponding setter is configured. Skip the setter → header is omitted.</p>
          <div className="mt-5 overflow-x-auto rounded-xl border border-white/10 bg-slate-900/60" data-testid="sdk-header-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                  <th className="text-left p-3">Header</th>
                  <th className="text-left p-3">Configure with</th>
                  <th className="text-left p-3">What it does</th>
                </tr>
              </thead>
              <tbody className="text-slate-300 font-mono">
                {[
                  ['X-Cartie-Key', 'set_proxy(key=…)', 'Authenticate the proxy. Required for every call.'],
                  ['X-Cartie-Tenant', 'set_tenant("marketing")', 'Per-department chargeback. Splits a shared bill.'],
                  ['X-Cartie-Source-Cloud', 'set_egress(source=…)', 'Egress preflight: source cloud (aws / gcp / azure / snowflake).'],
                  ['X-Cartie-Dest-Cloud', 'set_egress(dest=…)', 'Egress preflight: destination cloud.'],
                  ['X-Cartie-Data-GB', 'set_egress(data_gb=…)', 'Payload size for the preflight calculation.'],
                  ['X-Cartie-Token-Budget-Pct', 'set_egress(token_pct=…)', 'Tokens generated per GB of input (default 10%).'],
                  ['X-Cartie-Egress-Enforce', 'set_egress(enforce=True)', 'When true, proxy 412s rose verdicts before forwarding.'],
                ].map((row) => (
                  <tr key={row[0]} className="border-b border-white/5 last:border-0 hover:bg-emerald-500/5">
                    <td className="p-3 text-emerald-300 text-xs">{row[0]}</td>
                    <td className="p-3 text-slate-300 text-xs">{row[1]}</td>
                    <td className="p-3 text-slate-400 text-xs font-sans">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* DOWNLOAD / COPY THE SOURCE */}
        <div className="mt-12" data-testid="sdk-source-section">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">View the full source</h2>
            <div className="flex gap-1" data-testid="sdk-lang-tabs">
              {[
                { id: 'python',     label: 'Python' },
                { id: 'typescript', label: 'TypeScript' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActive(tab.id)}
                  className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded ${active === tab.id ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' : 'text-slate-400 hover:text-emerald-300 border border-transparent'}`}
                  data-testid={`sdk-tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="h-72 flex items-center justify-center rounded-xl border border-dashed border-white/10 bg-slate-900/40">
              <Loader2 className="w-6 h-6 animate-spin text-slate-600" />
            </div>
          ) : current ? (
            <div className="rounded-xl border border-white/10 bg-slate-900/60 overflow-hidden" data-testid="sdk-source-card">
              <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-white/10 bg-slate-950/60 flex-wrap">
                <div className="text-[11px] font-mono text-slate-400">
                  <Code2 className="w-3 h-3 inline mr-1" /> {current.filename} · {current.lines} lines · {(current.bytes / 1024).toFixed(1)} KB
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copy(current.source, current.language)}
                    className="text-[11px] font-mono uppercase tracking-widest text-slate-300 hover:text-emerald-300 inline-flex items-center gap-1"
                    data-testid="sdk-copy-btn"
                  >
                    {copied === current.language ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    {copied === current.language ? 'Copied' : 'Copy'}
                  </button>
                  {downloadUrl && (
                    <a
                      href={downloadUrl}
                      download={current.filename}
                      className="text-[11px] font-mono uppercase tracking-widest text-slate-300 hover:text-emerald-300 inline-flex items-center gap-1"
                      data-testid="sdk-download-btn"
                    >
                      <Download className="w-3 h-3" /> Download
                    </a>
                  )}
                </div>
              </div>
              <pre className="p-4 text-[11px] leading-relaxed font-mono text-slate-200 overflow-x-auto whitespace-pre max-h-[500px] overflow-y-auto" data-testid="sdk-source-pre">
                {current.source}
              </pre>
              <div className="px-4 py-2 border-t border-white/10 bg-slate-950/40 text-[11px] text-slate-500">
                {current.install_hint}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-300">
              Could not load the SDK source. Refresh the page or visit <a className="underline" href={downloadUrl || '#'}>the raw file</a> directly.
            </div>
          )}
        </div>

        {/* WHY THE SDK */}
        <div className="mt-12 grid md:grid-cols-3 gap-4" data-testid="sdk-why">
          {[
            { Icon: Sparkles, title: 'Zero per-call boilerplate', desc: 'Set the tenant once at app startup. Every call inherits it — no manual headers, no missed attributions.' },
            { Icon: Terminal, title: 'Drop-in compatible', desc: 'Returns the standard OpenAI / Anthropic client. All methods, all params, all streaming — unchanged.' },
            { Icon: Package, title: 'Single file. MIT.', desc: 'No PyPI / npm install. Just curl the file into your repo and import. ~250 lines. Auditable in 5 minutes.' },
          ].map((b) => {
            const Ic = b.Icon;
            return (
              <div key={b.title} className="rounded-xl border border-white/10 bg-slate-900/60 p-4">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 mb-2">
                  <Ic className="w-4 h-4 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                </div>
                <div className="text-sm font-bold text-white">{b.title}</div>
                <div className="text-xs text-slate-400 mt-1 leading-relaxed">{b.desc}</div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-8 text-center shadow-lg shadow-emerald-500/10" data-testid="sdk-cta">
          <h3 className="text-2xl font-extrabold text-white tracking-tight">Generate your first proxy key in 60 seconds</h3>
          <p className="text-slate-300 mt-2 max-w-xl mx-auto">Sign in, click "New proxy key" in the LLM Proxy panel, copy the secret into <code className="text-emerald-300 font-mono bg-slate-900 px-1.5 py-0.5 rounded">cartie.set_proxy(key=…)</code>, and you're live.</p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/signup" className="px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-md shadow-emerald-500/20 transition-all" data-testid="sdk-signup">
              Get a proxy key <ArrowRight className="w-4 h-4 inline ml-1" />
            </Link>
            <Link to="/virtual-tenant" className="px-6 py-2.5 rounded-lg border border-white/15 hover:border-emerald-500 hover:text-emerald-300 text-slate-300 text-sm font-semibold transition-all">
              See per-department breakdown
            </Link>
            <Link to="/egress-predictor" className="px-6 py-2.5 rounded-lg border border-white/15 hover:border-emerald-500 hover:text-emerald-300 text-slate-300 text-sm font-semibold transition-all">
              See egress predictor
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SdkPage;
